import type { AlertsConfig, Clause, FeederFieldConfig, PatternBranch } from '@kkp-fraud/shared';
import { DEFAULT_ALERTS } from '../alerts';
import { formatDiffValue } from '../diff';
import { FEEDER_FIELDS } from '../feederFields';
import { formatClauses, formatPatternRow, formatWindow } from '../format';
import { t } from '../i18n';
import { placeText } from '../ruleCheck';

/**
 * Field-level diff rows — the release dialog's "what changed" and the version history's
 * comparison. The props are loose (`LooseFieldChange`) so a diff read back from a stored
 * version renders as well as the one `diff.ts` computes from the draft.
 *
 * The proposal screen that also rendered a whole proposal diff here (`ProposalDiffView`) was
 * never shown and is gone (clean console, 30 Sep 2026).
 */

export interface LooseFieldChange {
  path: string;
  before: unknown;
  after: unknown;
}

export interface LooseRuleDiff {
  id: string;
  name: string;
  kind: string;
  status: string;
  changes: LooseFieldChange[];
}

/** Set-level paths that read better under a name than as the raw document path. */
const SETTING_LABELS: Record<string, string> = {
  feederFields: 'diff.meta.feederFields.label',
  alerts: 'diff.meta.alerts.label',
  // 2c rule sets
  setRules: 'rulesets.setRules.diff.label',
  // Wave 6
  customFields: 'diff.meta.customFields.label',
};

/**
 * A changed field as a person reads it: a set-level name, else the path in words (`clauses` →
 * "Conditions", `failSafe.ecommerceAction` → "Fail-safe · online"); a key the catalogue has no
 * word for stays as it is (QA-R-08).
 */
export function settingLabel(path: string): string {
  const key = SETTING_LABELS[path];
  return key ? t(key) : placeText(path);
}

/** A condition chain as its one-line sentence, or null when `v` is not one. */
function clausesText(v: unknown): string | null {
  return Array.isArray(v) ? formatClauses(v as Clause[]) : null;
}

/** Action rows, one "ACTION: condition" per row — a pattern row's measures as its sentence. */
function branchesText(v: unknown): string | null {
  if (!Array.isArray(v)) return null;
  return v
    .map((row: { action?: string; clauses?: Clause[]; measures?: unknown[] }) => {
      const condition = Array.isArray(row?.measures) ? formatPatternRow(row as PatternBranch) : formatClauses(row?.clauses ?? []);
      return `${row?.action ?? '?'}: ${condition}`;
    })
    .join(' · ');
}

/** One side of a changed rule field, worded where the field has a reading; else as stored. */
function ruleValue(field: string, v: unknown): string {
  if (v === undefined) return formatDiffValue(v);
  try {
    if (field === 'clauses' || field === 'filters') return clausesText(v) ?? formatDiffValue(v);
    if (field === 'branches') return branchesText(v) ?? formatDiffValue(v);
  } catch {
    // a shape the sentence cannot read (a hand-edited file) is shown as stored
    return formatDiffValue(v);
  }
  if (field === 'enabled' && typeof v === 'boolean') return t(v ? 'diff.value.on' : 'diff.value.off');
  if (field === 'windowSeconds' && typeof v === 'number') return formatWindow(v);
  if (field === 'requires' && Array.isArray(v)) return v.join(', ');
  return formatDiffValue(v);
}

const rowsOf = (v: unknown): readonly FeederFieldConfig[] => (Array.isArray(v) ? (v as FeederFieldConfig[]) : FEEDER_FIELDS);

/**
 * The feeder payload is 30-odd rows; the diff shows its size on each side and, on the new
 * side, which keys were added (+), removed (−) or changed (~) — not two walls of JSON.
 */
function feederValues(before: unknown, after: unknown): [string, string] {
  const a = rowsOf(before);
  const b = rowsOf(after);
  const byKey = new Map(a.map((r) => [r.key, JSON.stringify(r)]));
  const keysAfter = new Set(b.map((r) => r.key));
  const bits: string[] = [];
  for (const r of b) {
    const was = byKey.get(r.key);
    if (was === undefined) bits.push(`+ ${r.key}`);
    else if (was !== JSON.stringify(r)) bits.push(`~ ${r.key}`);
  }
  for (const r of a) if (!keysAfter.has(r.key)) bits.push(`− ${r.key}`);
  const count = (rows: readonly FeederFieldConfig[]) => t('fields.feeder.count', { n: rows.length });
  return [count(a), [count(b), ...bits].join(' · ')];
}

const alertsOf = (v: unknown): AlertsConfig =>
  v && typeof v === 'object' && Array.isArray((v as AlertsConfig).rules) ? (v as AlertsConfig) : DEFAULT_ALERTS;

/** `+ id` / `~ id` / `− id` for one list of records keyed by `id`. */
function idChanges(a: readonly { id: string }[], b: readonly { id: string }[]): string[] {
  const byId = new Map(a.map((r) => [r.id, JSON.stringify(r)]));
  const after = new Set(b.map((r) => r.id));
  const bits: string[] = [];
  for (const r of b) {
    const was = byId.get(r.id);
    if (was === undefined) bits.push(`+ ${r.id}`);
    else if (was !== JSON.stringify(r)) bits.push(`~ ${r.id}`);
  }
  for (const r of a) if (!after.has(r.id)) bits.push(`− ${r.id}`);
  return bits;
}

/** The alert configuration: its size on each side and, on the new side, which rules and channels changed. */
function alertValues(before: unknown, after: unknown): [string, string] {
  const a = alertsOf(before);
  const b = alertsOf(after);
  const count = (c: AlertsConfig) => t('alert.diff.count', { rules: c.rules.length, channels: c.channels.length });
  const bits = [...idChanges(a.rules, b.rules), ...idChanges(a.channels, b.channels)];
  return [count(a), [count(b), ...bits].join(' · ')];
}

/** Before/after as the diff table prints them. */
export function settingValues(path: string, before: unknown, after: unknown): [string, string] {
  if (path === 'feederFields') return feederValues(before, after);
  if (path === 'alerts') return alertValues(before, after);
  // 2c rule sets: how many set rules on each side, and which changed
  if (path === 'setRules') {
    const list = (v: unknown) => (Array.isArray(v) ? (v as { id: string }[]) : []);
    const count = (v: unknown) => t('rulesets.setRules.diff.count', { n: list(v).length });
    return [count(before), [count(after), ...idChanges(list(before), list(after))].join(' · ')];
  }
  // Wave 6: how many custom fields on each side, and which were bound, changed or unbound
  if (path === 'customFields') {
    const list = (v: unknown) => (Array.isArray(v) ? (v as { name: string }[]).map((f) => ({ ...f, id: f.name })) : []);
    const count = (v: unknown) => t('fields.custom.count', { n: list(v).length });
    return [count(before), [count(after), ...idChanges(list(before), list(after))].join(' · ')];
  }
  return [ruleValue(path, before), ruleValue(path, after)];
}

/** `added` / `removed` / `changed` in the active language; a status this build does not know as sent. */
export function statusLabel(status: string): string {
  return status === 'added' || status === 'removed' || status === 'changed' ? t(`diff.status.${status}`) : status;
}

export function ChangeRows({ changes }: { changes: readonly LooseFieldChange[] }) {
  return (
    <table>
      <tbody>
        {changes.map((c, i) => {
          const [before, after] = settingValues(c.path, c.before, c.after);
          return (
            <tr key={`${c.path}-${i}`}>
              <td className="nowrap" style={{ width: 200 }}>
                {settingLabel(c.path)}
              </td>
              <td className="mono small diff-before" style={{ width: '40%' }}>
                {before}
              </td>
              <td className="mono small diff-after">{after}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
